/**
 * galleryApi.ts — typed client for the LOCAL model gallery + profile pages.
 * Images are plain URLs (loaded by <img>), so only the JSON calls go through fetch.
 */

export interface ModelEntry {
  name: string;
  shootCount: number;
  hasPhoto: boolean;
}

export interface ProfileVideo {
  id: number;
  filename: string;
  videoNumber: number | null;
  vr: boolean;
  originalUrl: string | null;
  thumbIndex?: number; // owner view only (the public box doesn't expose the picker)
  candidates?: number; // how many candidate frames exist (owner view only)
}

export interface ProfileShoot {
  id: number;
  shootDate: string;
  models: string[];
  isVr: boolean;
  originalUrl: string | null;
  videos: ProfileVideo[];
}

const BASE = "/api";

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `${method} ${path} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export interface PublicSite {
  code: string;
  label: string;
}

/** The sites a webmaster can request a delivery to (NAMES ONLY, from the public box). */
export const listPublicSites = () => call<{ sites: PublicSite[] }>("GET", "/sites").then((r) => r.sites);

/** Webmaster submits a render request for a catalogued video → flows into the owner's queue. */
export const submitRequest = (body: { shootId: number; videoId: number; siteCode: string }) =>
  call<{ ok: boolean }>("POST", "/requests", { kind: "catalogued", payload: { shootId: body.shootId, videoId: body.videoId, siteCode: body.siteCode } });

// --- Webmaster magic-link auth (public site) ---
export interface Me {
  email: string;
  canSendToMain?: boolean; // may trigger "Send to main server" from the webmaster site (owner-allow-listed)
}
/** Current signed-in webmaster, or null if not signed in. */
export async function getMe(): Promise<Me | null> {
  const res = await fetch(`${BASE}/auth/me`);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`auth check failed (${res.status})`);
  return (await res.json()) as Me;
}
/** Sign in with email + the shared site password. */
export const login = (email: string, password: string) => call<{ ok: boolean; email: string }>("POST", "/auth/login", { email, password });
export const logout = () => call<{ ok: boolean }>("POST", "/auth/logout");

// --- Owner/ADMIN (separate password) — remote "Resume anyway" ---
export async function adminMe(): Promise<boolean> {
  const res = await fetch(`${BASE}/admin/me`);
  return res.ok;
}
export const adminLogin = (password: string) => call<{ ok: boolean }>("POST", "/admin/login", { password });
export const adminLogout = () => call<{ ok: boolean }>("POST", "/admin/logout");
export const adminResumeAll = () => call<{ ok: boolean }>("POST", "/admin/resume-all");

// --- Shared TEAM dashboard (everyone signed in sees ALL jobs + the full delivery log) ---
export interface ActiveJob {
  id: number;
  model: string;
  requester: string;
  site: string;
  statusText: string;
  renderPct: number;
  uploadPct: number;
  etaSec: number | null;
  bytes: number | null;
  updatedAt: string;
}
export interface DeliveredJob {
  id: number;
  model: string;
  requester: string;
  site: string;
  host: string | null;
  destFolder: string | null;
  filename: string | null;
  bytes: number | null;
  deliveredAt: string;
}
export interface ComingUpItem {
  models: string;
  shootDate: string;
  site: string | null;
}
export interface DashboardData {
  active: ActiveJob[];
  delivered: DeliveredJob[];
  comingUp: ComingUpItem[];
  pausedReason: string | null; // why waiting jobs haven't started (e.g. editor is using the workstation)
  updatedAt: string | null;
}
export const getDashboard = () => call<DashboardData>("GET", "/dashboard");

// --- Per-video delivery history (the "History" button) — same path on local + webmaster ---
export interface VideoHistoryEntry {
  site: string;
  requester: string;
  deliveredAt: string;
}
export const getVideoHistory = (videoId: number) => call<VideoHistoryEntry[]>("GET", `/video/${videoId}/history`);

// --- All shoots (for the "Shoots by date / latest added" gallery) ---
export interface ShootEntry {
  id: number; // catalog id — higher = more recently added
  shootDate: string;
  models: string[];
  isVr: boolean;
  videoIds: number[];
}
export const listAllShoots = () => call<ShootEntry[]>("GET", "/shoots");

export const listModels = () => call<ModelEntry[]>("GET", "/models");
export const modelShoots = (name: string) => call<ProfileShoot[]>("GET", `/models/${encodeURIComponent(name)}/shoots`);
export const setVideoOriginalUrl = (videoId: number, url: string) =>
  call<{ originalUrl: string | null }>("PUT", `/catalog/videos/${videoId}/original-url`, { url });

export const setVideoThumbnail = (videoId: number, index: number) =>
  call<{ thumbIndex: number }>("PUT", `/videos/${videoId}/thumbnail`, { index });

// --- "Send to main server" (the raw master → the main upload server, e.g. /4 DVD Files) ---
export interface MainSendStatus {
  status: "pending" | "sending" | "done" | "failed";
  detail: string | null;
  updatedAt: string;
  by: string;
}
/** LOCAL app (owner): send a master straight to the main server now; resolves when the transfer finishes. */
export const sendMasterToMainLocal = (videoId: number) =>
  call<{ ok: boolean; remotePath?: string; bytes?: number }>("POST", `/catalog/video/${videoId}/send-to-main`);
/** WEBMASTER site: queue a "send to main server"; the local app claims + fulfils it. */
export const requestMainSend = (videoId: number, label: string) =>
  call<{ ok: boolean; id: number; status: string; already?: boolean }>("POST", "/main-send", { videoId, label });
/** WEBMASTER site: latest send status per video id (for polling the button state). */
export const getMainSendStatus = (videoIds: number[]) =>
  call<Record<string, MainSendStatus>>("GET", `/main-send/status?videoIds=${videoIds.join(",")}`);

/** Image URLs (used directly in <img src>). */
export const modelPhotoUrl = (name: string) => `${BASE}/models/${encodeURIComponent(name)}/photo`;
export const thumbUrl = (videoId: number) => `${BASE}/thumbnails/${videoId}`;
export const candidateThumbUrl = (videoId: number, index: number) => `${BASE}/thumbnails/${videoId}/c/${index}`;
